'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNotification } from 'src/context/notification';
import { useLogging } from 'src/context/logging';
import { updateProviderCohort } from 'src/api/providerCohorts/providerCohortsApi';
import { useUpdateFilterSetMutation } from 'src/api/redux/filterSets';
import { useUpdateAdvancedSearchMutation } from 'src/api/redux/advancedSearches';
import type { AccountFolder } from 'src/api/redux/accountFolders';

type VolumeType = 'cohort' | 'filterSet' | 'advancedSearch';

interface UseVolumeDragAndDropParams {
  cohortsData: any;
  allCodeSets: any;
  advancedSearchesData: any;
  accountFoldersData: any;
  mutateAccountFolders: any;
}

/** Get the folder array key for a given volume type. */
const getFolderArrayKey = (
  volumeType: VolumeType
): 'provider_cohorts' | 'filter_sets' | 'advanced_searches' => {
  if (volumeType === 'cohort') return 'provider_cohorts';
  if (volumeType === 'filterSet') return 'filter_sets';
  return 'advanced_searches';
};

/** Check if an item matches a target id (handles both object and raw-id forms). */
const itemMatchesId = (item: any, targetId: number): boolean =>
  typeof item === 'object' ? item.id === targetId : item === targetId;

/** Find which folder (excluding `skipFolderId`) contains the item. */
const findCurrentFolder = (
  folders: AccountFolder[] | undefined,
  volumeType: VolumeType,
  volumeId: number,
  skipFolderId?: number
): { isInFolder: boolean; currentFolderId: number | null } => {
  if (!folders) return { isInFolder: false, currentFolderId: null };
  const key = getFolderArrayKey(volumeType);
  for (const folder of folders) {
    if (skipFolderId != null && folder.id === skipFolderId) continue;
    const arr = folder[key] as any[] | undefined;
    if (arr?.some((item) => itemMatchesId(item, volumeId))) {
      return { isInFolder: true, currentFolderId: folder.id };
    }
  }
  return { isInFolder: false, currentFolderId: null };
};

/**
 * Build an optimistic updater that removes `volumeId` from `currentFolderId`'s
 * array for the given `volumeType`.
 */
const buildFolderRemovalUpdater = (
  volumeType: VolumeType,
  volumeId: number,
  currentFolderId: number | null
) => {
  const key = getFolderArrayKey(volumeType);
  return (currentData: any) => {
    if (!currentData) return currentData;
    const updatedResults = currentData.results.map((folder: AccountFolder) => {
      if (folder.id !== currentFolderId) return folder;
      return {
        ...folder,
        [key]: ((folder[key] as any[]) ?? []).filter(
          (item) => !itemMatchesId(item, volumeId)
        ),
      };
    });
    return { ...currentData, results: updatedResults };
  };
};

/**
 * Drag-and-drop for saved volumes (cohorts, code sets, AI searches): moves an
 * item into a folder or into the personal/company section via the matching
 * update endpoint, with optimistic folder updates. Shared by the Discovery
 * saved-volumes dropdown and the Manage Code Sets drawer.
 */
export const useVolumeDragAndDrop = ({
  cohortsData,
  allCodeSets,
  advancedSearchesData,
  accountFoldersData,
  mutateAccountFolders,
}: UseVolumeDragAndDropParams) => {
  const [updateFilterSet] = useUpdateFilterSetMutation();
  const [updateAdvancedSearch] = useUpdateAdvancedSearchMutation();
  const { setNotification } = useNotification();
  const log = useLogging();
  const scrollIntervalsRef = useRef<Record<string, NodeJS.Timeout | null>>({});
  const isDraggingRef = useRef(false);
  const [dragOverSection, setDragOverSection] = useState<
    'personal' | 'company' | null
  >(null);

  /** Resolve the live record for a volume id so optimistic updates carry real data. */
  const resolveLiveRecord = useCallback(
    (volumeType: VolumeType, volumeId: number) => {
      if (volumeType === 'cohort')
        return cohortsData?.results?.find((c: any) => c.id === volumeId);
      if (volumeType === 'filterSet')
        return allCodeSets?.results?.find((fs: any) => fs.id === volumeId);
      return advancedSearchesData?.results?.find(
        (s: any) => s.id === volumeId
      );
    },
    [cohortsData, allCodeSets, advancedSearchesData]
  );

  /** Dispatch the correct API call to update folder_id and/or scope. */
  const dispatchUpdate = useCallback(
    async (
      volumeType: VolumeType,
      volumeId: number,
      data: Record<string, any>
    ) => {
      if (volumeType === 'cohort') {
        await updateProviderCohort(volumeId.toString(), data);
      } else if (volumeType === 'filterSet') {
        await updateFilterSet({ filterId: volumeId, data }).unwrap();
      } else {
        await updateAdvancedSearch({ id: volumeId, data }).unwrap();
      }
    },
    [updateFilterSet, updateAdvancedSearch]
  );

  const cleanupAllScrollIntervals = useCallback(() => {
    Object.values(scrollIntervalsRef.current).forEach((interval) => {
      if (interval) clearInterval(interval);
    });
    scrollIntervalsRef.current = {};
  }, []);

  useEffect(() => cleanupAllScrollIntervals, [cleanupAllScrollIntervals]);

  const handleDragStart = (e: React.DragEvent, volume: any) => {
    isDraggingRef.current = true;
    e.dataTransfer.setData(
      'text/plain',
      JSON.stringify({ id: volume.id, volumeType: volume.volumeType })
    );
  };

  const handleDragEnd = () => {
    isDraggingRef.current = false;
    setDragOverSection(null);
    cleanupAllScrollIntervals();
  };

  const handleDrop = async (e: React.DragEvent, folder: AccountFolder) => {
    e.preventDefault();
    isDraggingRef.current = false;
    cleanupAllScrollIntervals();

    try {
      const { id: volumeId, volumeType } = JSON.parse(
        e.dataTransfer.getData('text/plain')
      ) as { id: number; volumeType: VolumeType };

      const key = getFolderArrayKey(volumeType);
      const { isInFolder, currentFolderId } = findCurrentFolder(
        accountFoldersData?.results,
        volumeType,
        volumeId,
        folder.id
      );

      // Build optimistic target folder with the item added.
      const targetArr = (folder[key] as any[]) ?? [];
      const alreadyInFolder = targetArr.some((item) =>
        itemMatchesId(item, volumeId)
      );
      const updatedTargetFolder = { ...folder };
      if (!alreadyInFolder) {
        const live = resolveLiveRecord(volumeType, volumeId);
        const fallback =
          volumeType === 'filterSet'
            ? { id: volumeId, scope: folder.scope, label: '', type: 'CODE' }
            : { id: volumeId, scope: folder.scope, label: '', status: '' };
        (updatedTargetFolder as any)[key] = [...targetArr, live ?? fallback];
      }

      // Optimistic update: add to target, remove from source.
      mutateAccountFolders((currentData: any) => {
        if (!currentData) return currentData;
        const updatedResults = currentData.results.map(
          (f: AccountFolder) => {
            if (f.id === folder.id) return updatedTargetFolder;
            if (isInFolder && f.id === currentFolderId) {
              return {
                ...f,
                [key]: ((f[key] as any[]) ?? []).filter(
                  (item) => !itemMatchesId(item, volumeId)
                ),
              };
            }
            return f;
          }
        );
        return { ...currentData, results: updatedResults };
      }, false);

      await dispatchUpdate(volumeType, volumeId, { folder_id: folder.id });
      mutateAccountFolders();

      setNotification({
        title: 'Success',
        message: `Item moved to ${folder.label} folder`,
        type: 'success',
      });
    } catch (error) {
      log.exception(error, { tags: { context: 'useVolumeDragAndDrop.handleDrop' } });
      mutateAccountFolders();
      setNotification({
        title: 'Error',
        message: 'Failed to move item to folder',
        type: 'error',
      });
    }
  };

  const handleSectionDrop = async (
    e: React.DragEvent,
    targetScope: 'PERSONAL' | 'COMPANY'
  ) => {
    e.preventDefault();
    isDraggingRef.current = false;
    cleanupAllScrollIntervals();

    try {
      const { id: volumeId, volumeType } = JSON.parse(
        e.dataTransfer.getData('text/plain')
      ) as { id: number; volumeType: VolumeType };

      const volume = resolveLiveRecord(volumeType, volumeId);
      const currentScope = volume?.scope?.toUpperCase();
      const { isInFolder, currentFolderId } = findCurrentFolder(
        accountFoldersData?.results,
        volumeType,
        volumeId
      );

      if (!volume || (currentScope === targetScope && !isInFolder)) return;

      const updateData: Record<string, any> = {
        scope: targetScope.toLowerCase(),
      };

      if (isInFolder) {
        updateData.folder_id = null;
        mutateAccountFolders(
          buildFolderRemovalUpdater(volumeType, volumeId, currentFolderId),
          false
        );
      }

      await dispatchUpdate(volumeType, volumeId, updateData);
      mutateAccountFolders();

      setNotification({
        title: 'Success',
        message: `Item moved to ${targetScope.toLowerCase()} section`,
        type: 'success',
      });
    } catch (error) {
      log.exception(error, { tags: { context: 'useVolumeDragAndDrop.handleSectionDrop' } });
      // Revert the optimistic folder removal.
      mutateAccountFolders();
      setNotification({
        title: 'Error',
        message: 'Failed to move item to section',
        type: 'error',
      });
    }
  };

  const handleAccordionDragOver = (
    e: React.DragEvent,
    accordionType: 'personal' | 'company'
  ) => {
    e.preventDefault();
    if (!isDraggingRef.current) return;

    const container = e.currentTarget as HTMLElement;
    const rect = container.getBoundingClientRect();
    const isInTopTriggerZone = e.clientY - rect.top <= 50;

    if (isInTopTriggerZone && !scrollIntervalsRef.current[accordionType]) {
      const interval = setInterval(() => {
        if (container.scrollTop > 0) container.scrollTop -= 30;
      }, 100);
      scrollIntervalsRef.current[accordionType] = interval;
    } else if (
      !isInTopTriggerZone &&
      scrollIntervalsRef.current[accordionType]
    ) {
      clearInterval(scrollIntervalsRef.current[accordionType] ?? undefined);
      scrollIntervalsRef.current[accordionType] = null;
    }
  };

  const handleAccordionDragLeave = (
    accordionType: 'personal' | 'company'
  ) => {
    if (scrollIntervalsRef.current[accordionType]) {
      clearInterval(scrollIntervalsRef.current[accordionType] ?? undefined);
      scrollIntervalsRef.current[accordionType] = null;
    }
  };

  return {
    isDraggingRef,
    dragOverSection,
    setDragOverSection,
    handleDragStart,
    handleDragEnd,
    handleDrop,
    handleSectionDrop,
    handleAccordionDragOver,
    handleAccordionDragLeave,
    cleanupAllScrollIntervals,
  };
};
